import { getStoryBySlug } from '@cinch-nx/data-storyblok'
import { getNearestSites, getSites } from '@cinch-nx/shared-sites'
import { traceFunction } from '@core/datadog-server'
import {
  Site,
  SiteWithDistanceResponse,
  type SiteResponse,
} from '@cinch-labs/sites-sdk'
import { GetStaticPropsContext, InferGetStaticPropsType } from 'next'
import { CollectionLocationStory } from './storyblok/storyblok'
import { collectionLocationFallbackStory } from './storyblok/data/fallback-json'
import _ from 'lodash'

const isDev = process.env.STAGE_NAME !== 'production'
const STORYBLOK_BFF_URL = 'https://storyblok-bff.snc-prod.aws.cinch.co.uk'

async function getSiteBySlug(slug: string): Promise<SiteResponse | undefined> {
  try {
    const sites = await getSites({
      features: [{ attributeId: 'slug', searchValue: `wbac-${slug}` }],
    })

    if (sites.length === 0) return

    const site = sites[0]
    return { siteId: site.siteId, attributes: site.attributes }
  } catch {
    return
  }
}

async function getNearestWbacSitesToSite(
  postcode?: string,
): Promise<SiteWithDistanceResponse[] | undefined> {
  if (postcode === undefined) return

  try {
    const nearestSites = await getNearestSites(postcode, {
      filters: { tags: ['webuyanycar'] },
    })

    if (nearestSites.length === 0) return

    return nearestSites.map((siteWithDistance) => ({
      site: {
        siteId: siteWithDistance.site.siteId,
        attributes: siteWithDistance.site.attributes,
      },
      distance: siteWithDistance.distance,
    }))
  } catch {
    return
  }
}

async function getStory(preview: boolean) {
  try {
    return await getStoryBySlug<CollectionLocationStory>(
      'collection-location',
      {
        version: preview || isDev ? 'draft' : 'published',
      },
      { strict: true, urlDomain: STORYBLOK_BFF_URL },
    )
  } catch {
    return collectionLocationFallbackStory
  }
}

export async function getStaticPropsfn({
  params,
  preview = false,
}: GetStaticPropsContext) {
  const slug = params?.slug as string | undefined

  if (!slug) {
    return {
      notFound: true,
      revalidate: 60,
    }
  }

  const [story, site] = await Promise.all([
    getStory(preview),
    getSiteBySlug(slug),
  ])

  if (!site) {
    return {
      notFound: true,
      revalidate: 60,
    }
  }

  const siteInstance = new Site(site)
  const sitePostcode = siteInstance.getFeatureAttribute('address')?.postcode

  const nearestSitesResponse = await getNearestWbacSitesToSite(sitePostcode)

  const displayName = siteInstance.getFeatureAttribute('display_name')
  const slugBasedFallbackTitle = _.startCase(_.camelCase(slug))

  const heroProps = story.content.hero[0] ?? {}
  const heroPropsWithTitle = {
    ...heroProps,
    title: displayName ? displayName : slugBasedFallbackTitle,
  }

  return {
    props: {
      story,
      slugBasedFallbackTitle,
      site,
      nearestSitesResponse,
      preview,
      layoutProps: {
        heroProps: heroPropsWithTitle,
        showBorderRadius: true,
      },
    },
    revalidate: 60,
  }
}

export const getStaticProps = traceFunction(
  'collection-location.getStaticProps',
  getStaticPropsfn,
)

export type CollectionLocationProps = InferGetStaticPropsType<
  typeof getStaticProps
>
